import cloneDeep from 'lodash/fp/cloneDeep';
import { PaymentMethod, PaymentPeriod, YesNo, CreditType } from '../utils/enums';
import EcommerceService from '../services/ecommerceService';

const initialState = {
  vehiclePrice: null,
  provincialFees: null,
  freight: null,
  downPayment: null,
  // yesNo enum
  hasTradeIn: null,
  tradeInValue: null,
  // yesNo enum
  hasOutstandingTradeInBalance: null,
  outstandingTradeInBalance: null,
  selectedCreditRange: 'A',
  // ------- Payment calculator
  creditScoreUsedForPaymentCalculation: null,
  creditScoreTypeUsedForPaymentCalculation: null,
  buildAndPriceVehicleUsedForPaymentCalculation: null,
  paymentMethod: PaymentMethod.FINANCE,
  paymentSummaryData: null,
  paymentPeriod: PaymentPeriod.WEEKLY,
  paymentsAYear: 52,
  financePaymentTerm: null,
  leasePaymentTerm: null,
  mileage: null,
  cash: {
    salesTax: null,
    paymentAmount: null,
    preTaxRebates: null,
    postTaxRebates: null,
    preTaxRebatesTotal: null,
    postTaxRebatesTotal: null,
    luxuryTax: null,
  },
  finance: {
    interestRate: null,
    loanAmount: null,
    amountFinanced: null,
    salesTax: null,
    paymentAmount: null,
    preTaxRebates: null,
    postTaxRebates: null,
    preTaxRebatesTotal: null,
    postTaxRebatesTotal: null,
    costOfBorrowing: null,
    luxuryTax: null,
  },
  lease: {
    salesTax: null,
    residual: null,
    residualValue: null,
    moneyFactor: null,
    leaseAmount: null,
    paymentAmount: null,
    downPaymentTax: null,
    dueOnDelivery: null,
    preTaxVehiclePrice: null,
    preTaxRebates: null,
    postTaxRebates: null,
    preTaxRebatesTotal: null,
    postTaxRebatesTotal: null,
    luxuryTax: null,
  },
};

const getters = {
  currentPaymentAmount: state => state[`${state.paymentMethod}`].paymentAmount,
  currentPaymentTerm: state =>
    state[`${state.paymentMethod}PaymentTerm`] ? state[`${state.paymentMethod}PaymentTerm`] : null,
  taxRate: state => (state.paymentSummaryData ? parseFloat(state.paymentSummaryData.taxRate) : null),
};

const mutations = {
  SET_STATE(state, [key, data]) {
    state[key] = data;
  },
  SET_FINANCE_DATA(state, data) {
    state.finance = data;
  },
  SET_CASH_DATA(state, data) {
    state.cash = data;
  },
  SET_LEASE_DATA(state, data) {
    state.lease = data;
  },
  REMOVE_TRADE(state) {
    state.tradeInValue = null;
    state.hasTradeIn = YesNo.NO;
    state.hasOutstandingTradeInBalance = null;
    state.outstandingTradeInBalance = null;
  },
};

const actions = {
  // eslint-disable-next-line no-shadow
  async getPaymentCalculatorSettings({ rootState, commit }, payload) {
    const { isEcomV2, vehiclePrice } = payload;
    const { credit, business, paymentCalculator, vehicle, route, buildAndPrice } = rootState;
    const creditScore =
      credit.creditScore.creditGrade || credit.regionalCreditScore || paymentCalculator.selectedCreditRange;
    let res;
    if (isEcomV2) {
      res = await EcommerceService.getV2PaymentCalculatorSettings(
        business.api.key,
        business.api.externalID,
        creditScore,
        vehicle.vehicleInfo.status || 'used',
        vehicle.vehicleInfo.vin,
        vehiclePrice,
        route.query.language || 'en_CA',
        vehicle.vehicleInfo.year
      );
    } else {
      res = await EcommerceService.getBuildAndPricePaymentCalculatorSettings(
        business.api.key,
        business.api.externalID,
        creditScore,
        buildAndPrice.vehicleConfig.serializedValue,
        vehiclePrice,
        route.query.language || 'en_CA'
      );
      commit('SET_STATE', [
        'buildAndPriceVehicleUsedForPaymentCalculation',
        buildAndPrice.vehicleConfig.serializedValue,
      ]);
    }

    // Set credit score and type used
    let creditType = CreditType.USER_SUPPLIED;
    if (credit.creditScore.creditGrade) {
      creditType = CreditType.TRANSUNION_PERSONALIZED;
    } else if (credit.regionalCreditScore) {
      creditType = CreditType.EQUIFAX_REGIONAL;
    }
    commit('SET_STATE', ['creditScoreUsedForPaymentCalculation', creditScore]);
    commit('SET_STATE', ['creditScoreTypeUsedForPaymentCalculation', creditType]);
    commit('SET_STATE', ['paymentSummaryData', res.data]);
  },
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  mutations,
  actions,
  getters,
};
